import type { GridlineShape } from "sketch/shapes/gridline";
import {
  StateNode,
  createShapeId,
  maybeSnapToGrid,
  type Editor,
  type TLUiIconJsx,
  type TLUiOverrides,
  type TLUiToolItem,
} from "tldraw";

import { gridlineGhost } from "@/sketch/gridline-ghost";

// Not `tlui-icon`: that class masks a background color, blanking JSX icons.
// A drafter's gridline marker: the label bubble with its line hanging off.
const GridlineIcon: TLUiIconJsx = (
  <div style={{ width: 18, height: 18 }}>
    <svg viewBox="0 0 18 18" width="100%" height="100%" fill="none">
      <circle
        cx="9"
        cy="4.5"
        r="3.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="9"
        y1="8"
        x2="9"
        y2="18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3 1.5 1 1.5"
      />
    </svg>
  </div>
);

/** Drafting convention: letters across (x), numbers up (y). */
export function nextGridlineLabel(editor: Editor, axis: "x" | "y"): string {
  const used = new Set(
    editor
      .getCurrentPageShapes()
      .filter((s): s is GridlineShape => s.type === "gridline")
      .filter((s) => s.props.axis === axis)
      .map((s) => s.props.label),
  );
  if (axis === "y") {
    for (let n = 1; ; n++) if (!used.has(String(n))) return String(n);
  }
  for (let n = 0; ; n++) {
    const label =
      n < 26
        ? String.fromCharCode(65 + n)
        : `${String.fromCharCode(65 + Math.floor(n / 26) - 1)}${String.fromCharCode(65 + (n % 26))}`;
    if (!used.has(label)) return label;
  }
}

/**
 * A sticker, not a drawing tool: a ghost gridline (with the label it will
 * get, and a where-am-I chip) rides the cursor; click places it, shift
 * flips to horizontal (numbered). The tool stays active for the next one.
 */
export class GridlineTool extends StateNode {
  static override id = "gridline";

  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
    this.refreshGhost();
  }

  override onExit() {
    gridlineGhost.set(null);
    this.editor.setCursor({ type: "default", rotation: 0 });
  }

  override onPointerMove() {
    this.refreshGhost();
  }

  override onKeyDown(info: { key: string }) {
    if (info.key === "Escape") {
      this.editor.setCurrentTool("select");
      return;
    }
    this.refreshGhost(); // shift flips the ghost's axis live
  }

  override onKeyUp() {
    this.refreshGhost();
  }

  override onCancel() {
    this.editor.setCurrentTool("select");
  }

  override onPointerDown() {
    const ghost = gridlineGhost.get();
    if (!ghost) return;
    const id = createShapeId();
    this.editor.markHistoryStoppingPoint(`gridline:${id}`);
    // Both coordinates come from the ghost: the axis coordinate is the
    // gridline's position, the other only centers the finite render extent.
    this.editor.createShape<GridlineShape>({
      id,
      type: "gridline",
      x: ghost.x,
      y: ghost.y,
      props: { axis: ghost.axis, label: ghost.label },
    });
    this.refreshGhost(); // the next label advances
  }

  private refreshGhost() {
    const { currentPagePoint, shiftKey } = this.editor.inputs;
    const point = maybeSnapToGrid(currentPagePoint, this.editor);
    const axis = shiftKey ? "y" : "x";
    gridlineGhost.set({
      axis,
      x: point.x,
      y: point.y,
      label: nextGridlineLabel(this.editor, axis),
    });
  }
}

export const gridlineToolOverride: TLUiOverrides = {
  translations: {
    en: { "tool.gridline": "Gridline" },
  },
  tools(editor, tools) {
    tools.gridline = {
      id: GridlineTool.id,
      label: "tool.gridline",
      icon: GridlineIcon,
      // Bare g, per Revit's GR mnemonic; free in tldraw's default map.
      kbd: "g",
      onSelect() {
        editor.setCurrentTool(GridlineTool.id);
      },
    } satisfies TLUiToolItem;
    return tools;
  },
};
